import React from "react"
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	Grid,
	Select,
	MenuItem
} from "@material-ui/core"

import { IProductMessageMetrics } from "@/protocols/metrics"
import { MessageFeature } from "@/protocols/channel"

import { Portlet } from "@/components"

import useCustomStyles from "@/styles/custom"
import useStyles from "@/pages/Admin/Dashboard/Metrics/ProductMessageMetrics/LastDaysMetricsTableCard/styles"

import {
	isYesterday,
	isToday,
	getBrazilianWeekDay,
	formatDateInBrazilianDate
} from "@/utils/time"

export type LastDaysMetricsFilter = {
	lastDaysCount: number
	features: MessageFeature[]
}

type FilterOptions = {
	features: Array<{
		value: MessageFeature[]
		title: string
	}>
	lastDays: Array<{
		value: number
		title: string
	}>
}

type LastDaysMetricsTableCardProps = {
	messageMetricsByDate: IProductMessageMetrics["messageMetricsByDate"]
	changeLastDaysMetricsFilter: (filter: Partial<LastDaysMetricsFilter>) => void
	lastDaysMetricsFilter: LastDaysMetricsFilter
}

export const FILTER_OPTIONS: FilterOptions = {
	features: [
		{
			title: "Integração e Envio em massa",
			value: ["message-blast", "integration"]
		},
		{
			title: "Integração",
			value: ["integration"]
		},
		{
			title: "Envio em massa",
			value: ["message-blast"]
		}
	],
	lastDays: [
		{
			title: "Últimos 7 dias",
			value: 7
		},
		{
			title: "Últimos 30 dias",
			value: 30
		},
		{
			title: "Últimos 60 dias",
			value: 60
		}
	]
}

/**
 * Since we are using an array value inside select option, we need to use this workaround
 * to make sure the value will be correct selected in this component. Some bugs can
 * appear when using array values in select since the comparison is made by
 * reference, not the real array value.
 */
const FeaturesSerializer = {
	serialize: (features: MessageFeature[]): string => {
		return features.join(",")
	},
	deserialize: (serializedFeatures: string): MessageFeature[] => {
		return serializedFeatures.split(",") as MessageFeature[]
	}
}

const LastDaysMetricsTableCard: React.FC<LastDaysMetricsTableCardProps> = (props) => {
	const {
		messageMetricsByDate,
		changeLastDaysMetricsFilter,
		lastDaysMetricsFilter
	} = props

	const customClasses = useCustomStyles()
	const classes = useStyles()

	const getFormattedDate = (dateInString: string) => {
		const date = new Date(dateInString)

		const isYesterdayDate = isYesterday(date)
		const isTodayDate = isToday(date)

		const dateParams = []

		if (isTodayDate) {
			dateParams.push("Hoje")
		} else if (isYesterdayDate) {
			dateParams.push("Ontem")
		} else {
			dateParams.push(getBrazilianWeekDay(date))
		}

		dateParams.unshift(formatDateInBrazilianDate(date))

		const formattedDate = dateParams.join(" - ")

		return formattedDate
	}

	return (
		<Portlet>
			<Grid
				container
				spacing={2}
			>
				<Grid
					item
					xs={12}
				>
					<Grid
						container
						spacing={2}
					>
						<Grid
							item
							xs={12}
							md={3}
						>
							<Typography
								variant="h2"
								className={customClasses.uppercase}
							>
								Últimos {lastDaysMetricsFilter.lastDaysCount} dias
							</Typography>
						</Grid>

						<Grid
							item
							xs={12}
							md={9}
						>
							<Grid
								container
								spacing={2}
								justify="flex-end"
							>
								<Grid
									item
								>
									<Select
										fullWidth
										variant="outlined"
										value={FeaturesSerializer.serialize(lastDaysMetricsFilter.features)}
										onChange={({ target }) => changeLastDaysMetricsFilter({ features: FeaturesSerializer.deserialize(target.value as string) })}
									>
										{FILTER_OPTIONS.features.map((feature, index) => (
											<MenuItem
												value={FeaturesSerializer.serialize(feature.value)}
												key={index}
											>
												{feature.title}
											</MenuItem>
										))}
									</Select>
								</Grid>

								<Grid
									item
								>
									<Select
										fullWidth
										variant="outlined"
										value={lastDaysMetricsFilter.lastDaysCount}
										onChange={({ target }) => changeLastDaysMetricsFilter({ lastDaysCount: target.value as number })}
									>
										{FILTER_OPTIONS.lastDays.map((lastDays, index) => (
											<MenuItem
												value={lastDays.value}
												key={index}
											>
												{lastDays.title}
											</MenuItem>
										))}
									</Select>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Grid>

				<Grid
					item
					xs={12}
				>
					<Grid
						container
					>
						<TableContainer>
							<Table stickyHeader size="small">
								<TableHead>
									<TableRow>
										<TableCell>
											DATA
										</TableCell>

										<TableCell>
											ENVIADAS E NÃO ENTREGUES
										</TableCell>

										<TableCell>
											ENTREGUES
										</TableCell>

										<TableCell>
											COM ERRO
										</TableCell>
									</TableRow>
								</TableHead>

								<TableBody
									className={customClasses.reportTableBodyText}
								>
									{messageMetricsByDate?.map(messageMetricByDate => (
										<TableRow
											key={messageMetricByDate.date}
											tabIndex={-1}
										>
											<TableCell
												className={classes.date}
											>
												{getFormattedDate(messageMetricByDate.date)}
											</TableCell>

											<TableCell>
												{messageMetricByDate.sentButNotDeliveredMessages || 0}
											</TableCell>

											<TableCell>
												{messageMetricByDate.deliveredMessages || 0}
											</TableCell>

											<TableCell>
												{messageMetricByDate.notSentMessages || 0}
											</TableCell>
										</TableRow>
									))}
								</TableBody>

							</Table>
						</TableContainer>
					</Grid>
				</Grid>
			</Grid>
		</Portlet>
	)
}

export default LastDaysMetricsTableCard
